import { browserHistory } from 'react-router'
import _ from 'lodash'
import { http } from '../../../src/lib/http'
import Socket from '../../../src/lib/socket'
import constants from '../../lib/constants'
import { 
  currentStep as getCurrentStep,
  findForeplayStepByIndex,
} from '../../../src/lib/helper'

import drawBanner from '../../../src/assets/drawBanner.jpg'
import GameActionTypes from './types'
import UIActionTypes from '../ui/types'

const gameUpdate = ({ prop, value }) => ({
  type: GameActionTypes.GAME_UPDATE,
  payload: { prop, value },
})

const gameChangeRevealedFlag = (payload) => ({
  type: GameActionTypes.GAME_CHANGE_REVEALED_FLAG,
  payload,
})

const refreshGameAnswerStats = (payload) => ({
  type: GameActionTypes.REFRESH_GAME_STATS,
  payload,
})

const refreshPlayerList = (payload) => ({
  type: GameActionTypes.REFRESH_PLAYER_LIST,
  payload,
})

const startGame = () => ({
  type: GameActionTypes.GAME_ENABLE_GAME,
})

const disableForeplay = () => ({
  type: GameActionTypes.GAME_DISABLE_FOREPLAY,
})

const gameUpdateShowResults = (payload) => ({
  type: GameActionTypes.GAME_UPDATE_SHOW_RESULTS,
  payload,
})

const gameUpdateShowForeplayResults = (payload) => ({
  type: GameActionTypes.GAME_UPDATE_SHOW_FOREPLAY_RESULTS,
  payload,
})

const gameUpdateLog = (payload) => ({
  type: GameActionTypes.GAME_UPDATE_LOG,
  payload,
})

/**
 * Join the game success process
 * @param {fn} dispatch
 * @param {Object} response
 */
const joinGameSuccess = async (dispatch, response) => {
  const { result } = response.data
  const {
    game_state: gameState,
    game_config: gameConfig,
    ...game,
  } = result
  const {
    stage: gameStage,
    game_type: gameType,
  } = gameConfig
  const { game_stage_id } = gameState

  // Remove players from state as it would be duplicate
  const { players, ...gameStateWithoutPlayers } = gameState

  // Draft
  if (game_stage_id === constants.GAME_STAGE_DRAFT) {
    dispatch({
      type: UIActionTypes.UPDATE_ALERT_MESSAGE,
      payload: 'This game has not been started yet.',
    })
  } 

  // Complete
  if (game_stage_id === constants.GAME_STAGE_COMPLETE) {
    dispatch({
      type: UIActionTypes.UPDATE_ALERT_MESSAGE,
      payload: 'This game has been finished already.',
    })
  }

  // After joining the game successfully, it should
  // create the sockets instance
  Socket.closeSocket()
  await Socket.createSocket(game.game_id)

  /**
     * Create the current step object:
     *  - get the current segment and question from the game config 
     *    using the current game state.
     */
  let currentStep

  // Get the current step for a Game
  if (game_stage_id === constants.GAME_STAGE_RUNNING) {
    currentStep = getCurrentStep(gameState, gameStage)
  }

  // Get the current step for Foreplay
  if (game_stage_id === constants.GAME_STAGE_COMMENCING) {
    currentStep = findForeplayStepByIndex(gameStage, gameState.foreplay.step.index)
  }

  dispatch({
    type: GameActionTypes.JOIN_GAME_SUCCESS,
    payload: {
      ...game,
      gameStage,
      gameState: gameStateWithoutPlayers,
      currentStep,
      host: {
        user_id: gameConfig.game_type_options[gameType].host_id,
      },
    },
  })
  dispatch({
    type: GameActionTypes.GAME_UPDATE,
    payload: {
      prop: 'gameIdentifier',
      value: game.identifier
    },
  })

  // Game is RUNNING
  if (game_stage_id === constants.GAME_STAGE_RUNNING) {
    // when the current step is a question
    switch (currentStep.step.type) {
      case 'question':
        if (game.currentQuestionAnswers) {
          dispatch(refreshGameAnswerStats({ answers: game.currentQuestionAnswers }))
        }
        dispatch({
          type: UIActionTypes.UPDATE_GAME_STAGE,
          payload: 'questionsWindow',
        })
        break
      // when the current step is a segment
      case 'segment':

        // Dispatch an action to change the current Segment
        dispatch({
          type: UIActionTypes.UPDATE_CURRENT_SEGMENT,
          payload: currentStep.segment,
        })

        dispatch({
          type: UIActionTypes.UPDATE_GAME_STAGE,
          payload: 'segmentWindow',
        })
        break
      default:
        break
    }
  }

  // Game is COMMENCING
  if (game_stage_id === constants.GAME_STAGE_COMMENCING) {
    // Foreplay ended
    if (gameStage.foreplay.step_total -1 === gameState.foreplay.step.index) {
      dispatch({
        type: GameActionTypes.GAME_UPDATE_SHOW_FOREPLAY_RESULTS,
        payload: false,
      })
    } else {
      // Game is commencing, enable Foreplay
      dispatch({ type: GameActionTypes.GAME_ENABLE_FOREPLAY })
    
      // Jump the first Foreplay object because it is the segment description
      dispatch({ 
        type: GameActionTypes.GAME_CHANGE_FOREPLAY_INDEX,
        index: currentStep.step.index,
      })
      // Update the game stage
      dispatch({
        type: UIActionTypes.UPDATE_GAME_STAGE,
        payload: 'questionsWindow',
      })
    }
  }

  // Redirect the user to the right URL
  browserHistory.push(`/${game.game_id}`)
}

/**
 * Join the game failure process
 * @param {fn} dispatch
 * @param {object} response 
 */
const joinGameFailure = (dispatch) => {
  dispatch({
    type: UIActionTypes.UPDATE_ALERT_MESSAGE,
    payload: "That's an invalid game identifier!",
  })
  dispatch({
    type: GameActionTypes.JOIN_GAME_FAILURE,
  })
}

/**
 * Get game summary
 * @param {string} gameId
 * @param {fn} callback
 */
const getGameSummary = (gameId, callback) => {
  return () => {
    http.get(
      {
        url: `/game/stats`,
        params: {
          game_id: gameId,
        },
      }, (response) => {
        callback && callback(response.data.result)
      }
    )
  }
}

/**
 * Check if game exists
 * @param {Object} params
 */
const joinGame = (params) => {
  return (dispatch) => {
    http.get(
      {
        url: '/game/get',
        params,
      },
      (response) => joinGameSuccess(dispatch, response),
      () => joinGameFailure(dispatch)
    )
  }
}

/**
 * Refresh the game
 * @param {object} data 
 */
const refreshGame = (data) => {
  return (dispatch, getState) => {
    const payload = data.payload
    const { currentGame } = getState().game

    // Game ended
    if (!payload) {
      return
    }

    // Resets the showResults variable for the next question
    dispatch({
      type: GameActionTypes.GAME_UPDATE_SHOW_RESULTS,
      payload: false,
    })

    const currentStep = getCurrentStep({ game: payload.currentState }, currentGame.gameStage)

    dispatch({
      type: UIActionTypes.UPDATE_CURRENT_SEGMENT,
      payload: currentStep.segment,
    })

    switch (payload.currentState.step.type) {
      case 'question':
        // Dispatch an action to change the state of the game and re-render the UI
        dispatch({
          type: GameActionTypes.REFRESH_GAME,
          payload,
        })

        dispatch({
          type: UIActionTypes.UPDATE_GAME_STAGE,
          payload: 'questionsWindow',
        })

        // Update the segment banner
        dispatch({
          type: UIActionTypes.UPDATE_IMAGE_BANNER,
          payload: currentStep.segment.banner_live,
        })

        break
      case 'segment':
        dispatch({
          type: UIActionTypes.UPDATE_GAME_STAGE,
          payload: 'segmentWindow',
        })
        break
      default:
        break
    }
  }
}

/**
 * Refresh the foreplay state
 * @param {object} data {answers, index}
 */
const refreshForeplayStats = (data) => {
  return (dispatch, getState) => {
    // Jump the foreplay questions and refresh the foreplay stats
    const {
      gameStage,
      currentForeplayIndex,
    } = getState().game.currentGame

    dispatch({
      type: GameActionTypes.REFRESH_FOREPLAY_STATS,
      payload: data.answers,
    })

    if (currentForeplayIndex > data.index) {
      dispatch({
        type: GameActionTypes.GAME_CHANGE_FOREPLAY_INDEX,
        index: data.index,
      })
      return
    }

    // Reached the end of the foreplay results. Go back to lobby.
    if (gameStage.foreplay.step_total -1 === currentForeplayIndex) {
      dispatch({
        type: GameActionTypes.GAME_UPDATE_SHOW_FOREPLAY_RESULTS,
        payload: false,
      })
      dispatch(disableForeplay())
    } else {
      // Jump to the next question ONLY if the user is behind the current one
      if (data.index !== currentForeplayIndex) {
        dispatch({ type: GameActionTypes.GAME_JUMP_FOREPLAY })
      }
    }
  }
}

/**
 * Jump to the next foreplay question
 * when players hit the "Next" button
 */
const gameJumpForeplay = () => {
  return (dispatch, getState) => {
    const {
      gameStage,
      currentForeplayIndex,
    } = getState().game.currentGame

    // If its not the end of the Foreplay questions yet, keep displaying them
    if (gameStage.foreplay.step_total -1 > currentForeplayIndex) {
      dispatch({ type: GameActionTypes.GAME_JUMP_FOREPLAY })
    }
    
    // If it's already in the last foreplay question it should go back to the first one
    if (gameStage.foreplay.step_total -1 === currentForeplayIndex) {
      dispatch({
        type: GameActionTypes.GAME_CHANGE_FOREPLAY_INDEX,
        index: 0,
      })
    }
  }
}

/**
 * End the foreplay section
 */
const endForeplay = () => {
  return (dispatch) => {
    dispatch({
      type: GameActionTypes.GAME_UPDATE_SHOW_FOREPLAY_RESULTS,
      payload: false,
    })

    dispatch(disableForeplay())

    // Goes to the start window
    dispatch({
      type: UIActionTypes.UPDATE_GAME_STAGE,
      payload: 'startWindow',
    })
  }
}

/**
 * End the game
 */
const endGame = () => {
  return (dispatch, getState) => {
    const { game_id: gameId } = getState().game.currentGame

    // Navigate to the thankyou page
    browserHistory.push(`${gameId}/thankyou`)

    _.delay(() => {
      // Clean the game state
      dispatch({
        type: GameActionTypes.GAME_CLEAN_STATE,
      })
      // Clean the UI state
      dispatch({
        type: UIActionTypes.UI_CLEAN_STATE,
      })
    }, 1000)
  }
}

/**
 * Check for Draw
 */
const checkForDraw = () => {
  return (dispatch, getState) => {
    const {
      answerStats,
      currentStep,
    } = getState().game.currentGame

    // We avoid displaying Draws for Drag'n'Drop because there's no right/wrong
    if (answerStats.length > 0 && answerStats[0].rank !== undefined) {
      return
    }

    const orderedAnswers = _.orderBy(answerStats, 'percent', 'desc')
    let hasDraw

    if (orderedAnswers.length > 1) {
      hasDraw = orderedAnswers[0].percent === orderedAnswers[1].percent
    }

    if (hasDraw && currentStep.question.revealed) {
      dispatch({
        type: UIActionTypes.UPDATE_IMAGE_BANNER,
        payload: drawBanner,
      })
    }
  }
}

/**
 * Answer status reset
 * @param {object} log 
 */
const gameOnAnswerStatsReset = (log) => {
  return (dispatch, getState) => {
    dispatch({
      type: GameActionTypes.GAME_ON_ANSWER_STATS_RESET,
      payload: log,
    })

    const { banner_live } = getState().game.currentGame.currentStep.segment

    dispatch({
      type: UIActionTypes.UPDATE_IMAGE_BANNER,
      payload: banner_live,
    })
  }
}

export {
  gameUpdate,
  gameUpdateLog,
  gameOnAnswerStatsReset,
  gameChangeRevealedFlag,
  checkForDraw,
  startGame,
  refreshGameAnswerStats,
  disableForeplay,
  joinGame,
  gameJumpForeplay,
  getGameSummary,
  gameUpdateShowForeplayResults,
  gameUpdateShowResults,
  refreshForeplayStats,
  endForeplay,
  refreshGame,
  endGame,
  refreshPlayerList 
}



// WEBPACK FOOTER //
// ./src/entities/game/actions.js